// Repack a CaptureDevice tree node (from /camera-model/stores/{id}/tree/) into the
// videoRecorder shape that capabilitiesService + CameraModelDvrPanel consume — the same
// seam installationToVideoRecorderShape uses for the install wizard. The tree serializes
// `brand` and `inference_machine` as bare ids, so callers pass resolved lookup maps in.
//
// A RecorderBrand carries both a `kind` and a `name`, and they diverge: TVT and UNIVIEW are
// both `kind="IP"` because Simone falls back to its IP class for them, while update-dvr has
// brand-specific support and needs the actual name. Hence two fields: `video_recorder_type_name`
// keeps the kind every `=== "IP"` branch downstream already tests, and `video_recorder_brand_name`
// carries the name for callers that drive update-dvr.

function inferenceMachineDataFromId(inferenceMachineId, hostnameByMachineId) {
  if (inferenceMachineId == null) return null;
  const hostname = hostnameByMachineId?.[inferenceMachineId];
  return hostname ? { hostname } : null;
}

export default function captureDeviceToVideoRecorderShape(
  device,
  { brandKindById = {}, brandNameById = {}, hostnameByMachineId = {}, localInference = false } = {}
) {
  const cameras = Array.isArray(device?.cameras) ? device.cameras : [];
  const brandKind = device?.brand != null ? brandKindById[device.brand] ?? null : null;
  const brandName = device?.brand != null ? brandNameById[device.brand] ?? null : null;
  const firstCamera = cameras[0] ?? null;
  const subType = firstCamera?.stream_quality ? String(firstCamera.stream_quality).toLowerCase() : "main";
  // `rtsp_url_suffix` is per-camera in the camera model, but the legacy shape has room for only
  // one — so the first camera's stands for the device. On an EXPLICIT-url device every camera
  // carries a different suffix, which makes that pick arbitrary: flag it so callers that would
  // prefill a form field can leave it blank instead of showing one that fits a single camera.
  const uniformSuffix = new Set(cameras.map((camera) => camera?.rtsp_url_suffix ?? "")).size <= 1;

  const channels = cameras.map((camera) => ({
    channel_number: camera.slot_number,
    channel_name: null,
    protocol: camera.protocol ? String(camera.protocol).toLowerCase() : "tcp",
    inference_machine_data: inferenceMachineDataFromId(camera.inference_machine, hostnameByMachineId),
  }));

  // capabilitiesService picks device_api_port / device_rtsp_port for local inference and the
  // *_redirected fields for cloud, treating *_redirected as "the reachable port to connect to".
  // On a cloud store the reachable (public/router-facing) port is CaptureDevice's api_port/rtsp_port
  // while api_port_redirected/rtsp_port_redirected are the internal recorder ports — the opposite of
  // that expectation. Swap on cloud so the reachable ports land in the *_redirected fields.
  const rtspPort = localInference ? device?.rtsp_port : device?.rtsp_port_redirected;
  const rtspPortRedirected = localInference ? device?.rtsp_port_redirected : device?.rtsp_port;
  const apiPort = localInference ? device?.api_port : device?.api_port_redirected;
  const apiPortRedirected = localInference ? device?.api_port_redirected : device?.api_port;

  return {
    id: device?.public_id ?? null,
    device_ip: device?.host ?? null,
    device_rtsp_port: rtspPort ?? null,
    device_rtsp_port_redirected: rtspPortRedirected ?? null,
    device_api_port: apiPort ?? null,
    device_api_port_redirected: apiPortRedirected ?? null,
    username: device?.username ?? null,
    password: device?.password ?? null,
    rtsp_url_suffix: firstCamera?.rtsp_url_suffix ?? "",
    rtsp_url_suffix_uniform: uniformSuffix,
    type: brandKind ? { name: brandKind } : null,
    video_recorder_type_name: brandKind,
    video_recorder_brand_name: brandName,
    sub_type: subType,
    store: device?.store ?? null,
    channels,
  };
}
